import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TableLazyLoadEvent } from 'primeng/table';
import { AlarmService } from '../../../../../../../services/alarm.service';
import { DeviceProfileService } from '../../../../../../../services/device-profile.service';
import { DeviceService } from '../../../../../../../services/device.service';
import { InputSelectComponent } from '../../../../../../../shared/components/input-select/input-select';
import { TableComponent } from '../../../../../../../shared/components/table/table';
import { formatTimeAgo } from '../../../../../../../shared/utils/date.util';
import { AlarmDto, AlarmFindAndCountResponseDto } from '../../../../../../../types/dtos/alarm.dto';
import { DeviceDto } from '../../../../../../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../../../../../../types/dtos/deviceProfile.dto';
import { AlarmSeverity, AlarmStatus, AlarmType } from '../../../../../../../types/enums/alarm.enum';
import { InputSelectInterface } from '../../../../../../../types/interfaces/input.interface';
import { TableConfig, TableRowDto } from '../../../../../../../types/interfaces/table.interface';
import { DashboardBoxComponent } from '../../../../../../dashboard/dashboard-box/dashboard-box.component';
import { ReportAlarmRow } from '../../report-rows.dto';
import {TranslateService} from '@ngx-translate/core';
import {TranslateSafePipe} from '../../../../../../../shared/pipes/translation.pipe';


@Component({
  selector: 'app-report-filter-alarm',
  imports: [
    TableComponent,
    InputSelectComponent,
    DashboardBoxComponent,
    TranslateSafePipe,
  ],
  templateUrl: './filter-alarm.html',
  styleUrl: './filter-alarm.css',
})
export class ReportFilterAlarm {
  @Input()
  public dateFrom: Date | null = null;

  @Input()
  public dateTo: Date | null = null;

  @Input({required: true}) formGroup!: FormGroup;
  public loading: boolean = true;
  public tableRows: TableRowDto<ReportAlarmRow>[] = [];
  public readonly tableConfig: TableConfig<ReportAlarmRow> = {
    columns: [
      {
        key: 'status',
        isTranslated: true,
        title: 'common.table.status.label',
        isTag: true,
        overrideValue: (row: ReportAlarmRow) => this._alarmService.mapAlarmStatusLabel(row.status),
        getTagColor: (row: ReportAlarmRow) => this._alarmService.mapAlarmStatusClass(row.status),
      },
      {
        key: 'severity',
        isTranslated: true,
        title: 'common.table.severity.label',
        isTag: true,
        overrideValue: (row: ReportAlarmRow) => this._alarmService.mapAlarmSeverityLabel(row.severity),
        getTagColor: (row: ReportAlarmRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
      },
      {
        key: 'device_eui',
        isTranslated: true,
        title: 'common.table.serielNumber.label',
      },
      {
        key: 'model',
        isTranslated: true,
        title: 'common.table.model.label',
      },
      {
        key: 'message',
        isTranslated: true,
        title: 'common.table.message.label',
      },
      {
        key: 'address',
        isTranslated: true,
        title: 'common.table.address.label',
      },
      {
        key: 'created_at',
        isTranslated: true,
        title: 'common.table.created-at.label',
        overrideValue: (row: ReportAlarmRow) => formatTimeAgo(row.created_at),
      },
    ],
    mobile: {
      tags: [
        {
          key: 'status',
          overrideValue: (row: ReportAlarmRow) => this._alarmService.mapAlarmStatusLabel(row.status),
          getColor: (row: ReportAlarmRow) => this._alarmService.mapAlarmStatusClass(row.status),
        },
        {
          key: 'severity',
          overrideValue: (row: ReportAlarmRow) => this._alarmService.mapAlarmSeverityLabel(row.severity),
          getColor: (row: ReportAlarmRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
        },
      ],
      title: {
        key: 'message',
      },
      sections: [
        {
          key: 'device_eui',
          isTranslated: true,
          title: 'common.table.devices.label',
        },
        {
          key: 'address',
          isTranslated: true,
          title: 'common.table.address.label',
        },
        {
          key: 'created_at',
          isTranslated: true,
          title: 'common.table.created-at.label',
          overrideValue: (row: ReportAlarmRow) => formatTimeAgo(row.created_at),
        },
      ],
    },
    pagination: true,
    showRows: 25,
    rowsPerPageOptions: [
      25,
    ],
    search: {
      label: 'common.messages.info.search-alarms',
      id: 'report-alarm-global-search',
    },
    scrollHeight: '35vh',
    emptyMessage: 'common.messages.empty.no-alarms-available',
    lazy: true,
    onLazyLoad: (event: TableLazyLoadEvent) => {
      this.loadAlarms(
        event.rows ?? undefined,
        event.first ?? undefined,
      );
    },
  };
  private limit: number = this.tableConfig.showRows;
  private offset: number = 0;

  constructor(
    private _deviceService: DeviceService,
    private _deviceProfileService: DeviceProfileService,
    private _alarmService: AlarmService,
    private readonly _translateService: TranslateService,
  ) {
  }

  public get alarmCountLabel(): string {
    return `${ this.tableConfig.totalRecords } ${this._translateService.instant('common.label.alarms-found')}`;
  }

  public get statusFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.fields.status',
      id: 'status',
      iconLeft: 'check_circle',
      showClear: true,
      options: Object.values(AlarmStatus)
        .map((status: AlarmStatus) => ({
          label: this._alarmService.mapAlarmStatusLabel(status),
          value: status,
        })),
      onInput: () => this.loadAlarms(),
    };
  };

  public get severityFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.severity.label',
      id: 'severity',
      iconLeft: 'priority_high',
      showClear: true,
      options: Object.values(AlarmSeverity)
        .map((severity: AlarmSeverity) => ({
          label: this._alarmService.mapAlarmSeverityLabel(severity),
          value: severity,
        })),
      onInput: () => this.loadAlarms(),
    };
  };

  public get alarmTypeFilterConfig(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.table.alarm-type.label',
      id: 'alarmType',
      iconLeft: 'notification_important',
      showClear: true,
      options: Object.values(AlarmType)
        .map((status: AlarmType) => ({
          isTranslated: true,
          label: 'common.enum.alarm-type.' + status,
          value: status,
        })),
      onInput: () => this.loadAlarms(),
    };
  };


  private async loadAlarms(
    limit: number = this.limit,
    offset: number = this.offset,
  ): Promise<void> {
    this.loading = true;

    const severity: AlarmSeverity | undefined = (this.formGroup.get('severity')?.value) ?
      this.formGroup.get('severity')?.value as AlarmSeverity
      : undefined;
    const alarm_type: AlarmType | undefined = (this.formGroup.get('alarmType')?.value) ?
      this.formGroup.get('alarmType')?.value as AlarmType
      : undefined;
    const status: AlarmStatus | undefined = (this.formGroup.get('status')?.value) ?
      this.formGroup.get('status')?.value as AlarmStatus
      : undefined;

    this._alarmService.getAll({
      limit,
      offset,
      ...(status ? {status: [status]} : {}),
      ...(severity ? {severity: [severity]} : {}),
      ...(alarm_type ? {alarm_type: [alarm_type]} : {}),
    })
      .subscribe({
        next: (alarmResponse: AlarmFindAndCountResponseDto) => {
          this.tableRows = (alarmResponse.data ?? []).map((alarm: AlarmDto) => {
            let device: DeviceDto | null = this._deviceService.getLocalById(alarm.device_id);
            let deviceProfile: DeviceProfileDto | null = null;

            if (device?.device_profile_id) {
              deviceProfile = this._deviceProfileService.getDeviceProfileById(device?.device_profile_id);
            }

            return {
              id: alarm.id,
              status: alarm.status,
              message: alarm.message,
              severity: alarm.severity,
              device_eui: device?.serial_number ?? device?.device_eui ?? '-',
              model: deviceProfile?.model ?? '-',
              address: device?.position?.address ?? '-',
              created_at: alarm.created_at,
            };
          });

          this.tableConfig.totalRecords = alarmResponse.rowCount;

          this.limit = limit;
          this.offset = offset;
        },
        error: (error: unknown) => {
          console.error('Error fetching alarms:', error);

          this.tableRows = [];
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

}
